import React, { useMemo, useState } from 'react';
import { AlertTriangle, Check, Plus, Search, X } from 'lucide-react';

import { Button } from '../ui/Button';
import { CreateProjectModal } from '../layout/ProjectContextHeader';
import { getProjectDisplayName } from '../../lib/utils';
import {
  EstimateHeaderMetadata,
  NewProjectForm,
  Project,
  ProjectMatchResponse,
} from '../../types';

// ================================================================= //
//          PostParseProjectDialog                                   //
// ================================================================= //
// Confirmation dialog rendered after a PDF parse on the              //
// Completeness page. Five visual states, derived from whether the    //
// user already has a project selected and whether the matcher        //
// returned anything:                                                  //
//                                                                     //
//   selected + no match          -> "Import into <current>?"          //
//   selected + exact == current  -> never rendered (caller skips)     //
//   selected + exact != current  -> conflict picker                   //
//   no project + no match        -> "Create new" primary              //
//   no project + exact match     -> "Use <name> or create new?"       //
//   no project + fuzzy matches   -> show suggestions list             //
// ================================================================= //

export interface PostParseProjectDialogProps {
  /** The header_metadata payload from the parse response. */
  header: EstimateHeaderMetadata;
  /** Match result from POST /user/projects/match. */
  match: ProjectMatchResponse | null;
  /** Currently-selected project (from ?project=<id>). */
  currentProject: Project | null;
  /** Called when the user picks "Import into <project>". */
  onUseProject: (project: Project) => void;
  /** Called when a new project is created via the embedded modal. */
  onProjectCreated: (project: Project) => void;
  /** Dismiss without picking anything (caller still keeps parse output). */
  onClose: () => void;
}

/**
 * Build CreateProjectModal initialValues from extracted header metadata.
 */
function buildInitialValues(
  header: EstimateHeaderMetadata,
): Partial<NewProjectForm & { policy_number?: string; loss_type?: string; claim_type?: string }> {
  return {
    street: header.address?.street ?? '',
    city: header.address?.city ?? '',
    state: header.address?.state ?? '',
    zip: header.address?.zip ?? '',
    owner_name: header.owner_name ?? '',
    claim_number: header.claim_number ?? '',
    policy_number: header.policy_number ?? '',
    loss_type: header.loss_type ?? '',
    claim_type: header.claim_type ?? '',
  };
}

export default function PostParseProjectDialog({
  header,
  match,
  currentProject,
  onUseProject,
  onProjectCreated,
  onClose,
}: PostParseProjectDialogProps) {
  const [showCreateModal, setShowCreateModal] = useState(false);

  const exactMatch = match?.exact_match ?? null;
  const fuzzyMatches = match?.fuzzy_matches ?? [];

  // Reason: a tidy display string for the header so the user can confirm
  // we're looking at the right estimate before they pick a destination.
  const headerSummary = useMemo(() => {
    const parts: string[] = [];
    if (header.owner_name) parts.push(header.owner_name);
    if (header.address?.street) parts.push(header.address.street);
    if (header.claim_number) parts.push(`Claim ${header.claim_number}`);
    return parts.join(' · ');
  }, [header]);

  // ----- State classification -----
  // Five mutually-exclusive dialog states. The caller is responsible for
  // NOT rendering this component when selected + exact == current (the
  // "obvious answer — just import" path).
  type DialogState =
    | 'selected-no-match'
    | 'selected-conflict'
    | 'no-project-no-match'
    | 'no-project-exact'
    | 'no-project-fuzzy';

  const state: DialogState = useMemo(() => {
    if (currentProject) {
      if (exactMatch && exactMatch.id !== currentProject.id) {
        return 'selected-conflict';
      }
      return 'selected-no-match';
    }
    if (exactMatch) return 'no-project-exact';
    if (fuzzyMatches.length > 0) return 'no-project-fuzzy';
    return 'no-project-no-match';
  }, [currentProject, exactMatch, fuzzyMatches.length]);

  const initialValues = useMemo(() => buildInitialValues(header), [header]);

  // The CreateProjectModal is rendered as an overlay on top of (or
  // instead of) this one when the user chooses "create new".
  if (showCreateModal) {
    return (
      <CreateProjectModal
        title="Create project from estimate"
        subtitle="Prefilled from the estimate header. Edit anything before saving."
        submitLabel="Create and use"
        initialValues={initialValues}
        onClose={() => setShowCreateModal(false)}
        onCreated={onProjectCreated}
      />
    );
  }

  // Title + body vary by state.
  let title = 'Where should this estimate go?';
  let body: React.ReactNode = null;
  let actions: React.ReactNode = null;

  if (state === 'selected-no-match') {
    title = `Import into ${getProjectDisplayName(currentProject!)}?`;
    body = (
      <p className="text-sm text-muted-foreground">
        No existing project matches this estimate. You can import it into the
        currently selected project or create a new project from the estimate
        header.
      </p>
    );
    actions = (
      <>
        <Button variant="ghost" size="sm" onClick={() => setShowCreateModal(true)}>
          <Plus className="-ml-0.5 mr-1 h-3.5 w-3.5" />
          Create new project
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => onUseProject(currentProject!)}
        >
          <Check className="-ml-0.5 mr-1 h-3.5 w-3.5" />
          Import here
        </Button>
      </>
    );
  } else if (state === 'selected-conflict') {
    title = 'Estimate matches a different project';
    body = (
      <div className="space-y-3">
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            This estimate&apos;s claim number matches{' '}
            <strong>{getProjectDisplayName(exactMatch!)}</strong>, but your
            currently selected project is{' '}
            <strong>{getProjectDisplayName(currentProject!)}</strong>.
          </span>
        </div>
      </div>
    );
    actions = (
      <>
        <Button variant="ghost" size="sm" onClick={() => setShowCreateModal(true)}>
          <Plus className="-ml-0.5 mr-1 h-3.5 w-3.5" />
          Create new
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onUseProject(currentProject!)}
        >
          Stay in {getProjectDisplayName(currentProject!)}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => onUseProject(exactMatch!)}
        >
          Switch to {getProjectDisplayName(exactMatch!)}
        </Button>
      </>
    );
  } else if (state === 'no-project-exact') {
    title = `Use ${getProjectDisplayName(exactMatch!)}?`;
    body = (
      <p className="text-sm text-muted-foreground">
        This estimate&apos;s claim number matches an existing project. You
        can switch to it or create a new project from the estimate header.
      </p>
    );
    actions = (
      <>
        <Button variant="ghost" size="sm" onClick={() => setShowCreateModal(true)}>
          <Plus className="-ml-0.5 mr-1 h-3.5 w-3.5" />
          Create new
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => onUseProject(exactMatch!)}
        >
          Use {getProjectDisplayName(exactMatch!)}
        </Button>
      </>
    );
  } else if (state === 'no-project-fuzzy') {
    title = 'Possible existing projects';
    body = (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          We found similar existing projects. Pick one to import into, or
          create a new project from the estimate header.
        </p>
        <ul className="divide-y divide-slate-100 rounded-md border">
          {fuzzyMatches.map((p) => {
            const score = p._match_score ?? 0;
            const confidence =
              score >= 0.9 ? 'High' : score >= 0.8 ? 'Medium' : 'Low';
            return (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => onUseProject(p)}
                  className="flex w-full items-start justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50"
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-medium text-foreground">
                      {getProjectDisplayName(p)}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {[p.address, p.claim_number]
                        .filter(Boolean)
                        .join(' · ') || 'No address'}
                    </span>
                  </div>
                  <span className="flex-shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                    {confidence} match
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
    actions = (
      <Button
        variant="primary"
        size="sm"
        onClick={() => setShowCreateModal(true)}
      >
        <Plus className="-ml-0.5 mr-1 h-3.5 w-3.5" />
        Create new project
      </Button>
    );
  } else {
    // no-project-no-match
    title = 'Create project from this estimate?';
    body = (
      <p className="text-sm text-muted-foreground">
        No matching project found. You can create a new project prefilled
        from the estimate header, or match this estimate to an existing
        project manually.
      </p>
    );
    actions = (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={onClose}
          aria-label="Match to existing project"
        >
          <Search className="-ml-0.5 mr-1 h-3.5 w-3.5" />
          Match to existing
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowCreateModal(true)}
        >
          <Plus className="-ml-0.5 mr-1 h-3.5 w-3.5" />
          Create new project
        </Button>
      </>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Project import options"
      data-testid="post-parse-dialog"
      data-state={state}
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-20"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl rounded-lg border bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 border-b pb-3">
          <div>
            <h4 className="text-base font-semibold text-foreground">{title}</h4>
            {headerSummary && (
              <p className="mt-1 text-xs text-muted-foreground">
                From estimate: {headerSummary}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-3">{body}</div>

        <div className="mt-4 flex flex-wrap justify-end gap-2 border-t pt-3">
          {actions}
        </div>
      </div>
    </div>
  );
}
